<template>
  <div>
    <transition name="el-fade-in">
      <div
        v-if="settings.title"
        :class="{
          effect,
          'keyreply-shadow': settings.launcherShadow !== false,
          'keyreply-medium': !$device.mobile && settings.size !== 'large',
          'keyreply-large': !$device.mobile && settings.size === 'large',
          'keyreply-disabled': $store.getters.webchatUsersIsAtMaxLimit,
        }"
        @click="launcherClicked"
        class="keyreply-launcher"
        id="kr-launcher"
      >
        <Badge />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Badge from "./Badge";
import advancedFormat from "dayjs/plugin/advancedFormat";
import dayjs from "dayjs";
dayjs.extend(advancedFormat);
export default {
  name: "Launcher",
  data() {
    return {
      effect: "",
    };
  },
  props: ["isLauncherCanClick"],
  components: {
    Badge,
  },
  computed: {
    ...mapGetters({
      settings: "settings",
    }),
  },
  watch: {
    settings: {
      handler(after, before) {
        if (!this.$store.state.chatWindow && after && after.openAutomatically === true) {
          const lastOpenStatus = localStorage.getItem("keyreply-lastOpenStatus");
          const lastSeen = dayjs(localStorage.getItem("_krlastseen") || undefined);
          const now = dayjs();

          const isSameDay = now.isSame(lastSeen, "day");

          // session_length is in minutes
          const isSameSession =
            now.diff(lastSeen, "minutes") < this.$store.state.settings.session_length;

          if (lastOpenStatus == "true" && isSameSession) {
            this.launcherClicked();
          }
        }
      },
      deep: true,
    },
  },
  methods: {
    launcherClicked() {
      if (!this.$store.getters.webchatUsersIsAtMaxLimit && this.isLauncherCanClick) {
        this.effect = "keyreply-show-effect";
        if (
          this.$parent.numberOfApps === 1 &&
          this.settings.apps.bot &&
          this.settings.apps.bot.enabled
        ) {
          this.$store.dispatch("START");
          this.$store.dispatch("TOGGLE_CHAT_WINDOW", this.$device.iOSSDK);
        } else {
          this.$parent.showIcons = !this.$parent.showIcons;
        }

        setTimeout(() => {
          this.effect = "";
        }, 700);

        this.$emit("click");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.keyreply-launcher {
  transition: color 0.7s;
  -webkit-transition: color 0.7s;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  background-size: contain;
  cursor: pointer;
  position: static;

  width: 50px;
  height: 50px;

  pointer-events: auto;

  border-radius: var(--keyreply-launcher-border-radius);
  background-color: var(--keyreply-primary-color);
  background-image: var(--keyreply-launcher-background-image);

  &.keyreply-shadow {
    box-shadow: 0 8px 11px rgba(71, 69, 123, 0.25), 0 2px 4px rgba(71, 69, 123, 0.12);
  }

  &.keyreply-disabled {
    cursor: not-allowed;
  }

  &.keyreply-medium {
    width: 64px;
    height: 64px;
    bottom: 32px;
  }

  &.keyreply-large {
    width: 100px;
    height: 100px;
    max-height: 100px;
    max-width: 100px;
    min-height: 50px;
    bottom: 22px;

    @media only screen and (max-width: 380px) {
      width: 75px;
      height: 75px;
      max-height: 75px;
      max-width: 75px;
    }

    @media only screen and (max-width: 320px) {
      width: 50px;
      height: 50px;
      max-height: 50px;
      max-width: 50px;
    }
  }
}

[dir="ltr"] .keyreply-launcher {
  float: left;

  &.keyreply-medium {
    right: 26px;
  }

  &.keyreply-large {
    right: 18px;
  }
}

[dir="rtl"] .keyreply-launcher {
  float: right;

  &.keyreply-medium {
    left: 26px;
  }

  &.keyreply-large {
    left: 18px;
  }
}

.launcher-subtext {
  position: relative;
  top: 52px;
  text-align: center;
  font-size: 0.8em;
  transition: color 0.5s ease;

  &:hover {
    color: #0645ad;
    -webkit-transform: scale(1.3);
    -ms-transform: scale(1.3);
    transform: scale(1.3);
    text-decoration: underline;
  }
}

/* Click Effects */
.keyreply-effect:before {
  position: absolute;
  top: 50%;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  content: "";
  opacity: 0;
  filter: alpha(opacity=0);
  pointer-events: none;
}

[dir="ltr"] .keyreply-effect:before {
  left: 50%;
  margin: -50px 0 0 -50px;
}

[dir="rtl"] .keyreply-effect:before {
  right: 50%;
  margin: -50px -50px 0 0;
}

.keyreply-effect:before,
.keyreply-effect:after {
  box-shadow: 0 0 0 2px rgba(111, 148, 182, 0.6);
}

.keyreply-effect.keyreply-show-effect:before {
  -webkit-animation: keyreply-effect-1 0.5s forwards;
  animation: keyreply-effect-1 0.5s forwards;
}

.keyreply-effect.keyreply-show-effect:after {
  -webkit-animation: keyreply-effect-2 0.5s forwards;
  animation: keyreply-effect-2 0.5s forwards;
}

@-webkit-keyframes keyreply-effect-1 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.1, 1.1, 1);
    transform: scale3d(1.1, 1.1, 1);
  }
}

@keyframes keyreply-effect-1 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.1, 1.1, 1);
    transform: scale3d(1.1, 1.1, 1);
  }
}

@-webkit-keyframes keyreply-effect-2 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  50%,
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.2, 1.2, 1);
    transform: scale3d(1.2, 1.2, 1);
  }
}

@keyframes keyreply-effect-2 {
  0% {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: scale3d(0.5, 0.5, 1);
    transform: scale3d(0.5, 0.5, 1);
  }
  50%,
  100% {
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: scale3d(1.2, 1.2, 1);
    transform: scale3d(1.2, 1.2, 1);
  }
}
</style>
